import { useCallback, useEffect, useRef, useState } from 'react';
import type { WhatsappSession, WhatsappStatus } from '@supporchat/shared';
import { api } from '../lib/api';
import { formatPhone } from '../components/CallBar';

interface Props {
  isAdmin: boolean;
  sessionEvent: WhatsappSession | null;
  qrEvent: { sessionId: string; qrCode: string } | null;
}

const STATUS_LABELS: Record<WhatsappStatus, string> = {
  disconnected: 'Desconectado',
  connecting: 'Conectando',
  qr_required: 'Aguardando leitura do QR',
  connected: 'Conectado',
  logged_out: 'Desvinculado',
  error: 'Com erro',
};

export function ConnectionPage({ isAdmin, sessionEvent, qrEvent }: Props) {
  const [sessions, setSessions] = useState<WhatsappSession[]>([]);
  const [name, setName] = useState('Atendimento');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(await api.listSessions());
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // O QR expira em segundos e é renovado pelo servidor; por isso ele chega por
  // WebSocket em vez de ser buscado por polling.
  useEffect(() => {
    if (!sessionEvent) return;
    setSessions((current) =>
      current.some((item) => item.id === sessionEvent.id)
        ? current.map((item) => (item.id === sessionEvent.id ? sessionEvent : item))
        : [...current, sessionEvent],
    );
  }, [sessionEvent]);

  useEffect(() => {
    if (!qrEvent) return;
    setSessions((current) =>
      current.map((item) =>
        item.id === qrEvent.sessionId ? { ...item, qrCode: qrEvent.qrCode, status: 'qr_required' } : item,
      ),
    );
  }, [qrEvent]);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page__inner">
        {error && <div className="alert alert--error">{error}</div>}
        {!isAdmin && <div className="alert alert--info">Apenas administradores gerenciam a conexão.</div>}

        {sessions.length === 0 && (
          <div className="card">
            <h3 className="card__title">Conectar um número de WhatsApp</h3>
            <p className="card__subtitle">
              Use um número dedicado ao atendimento. Ele é pareado como dispositivo vinculado, do mesmo jeito
              que o WhatsApp Web.
            </p>

            <div className="field">
              <label htmlFor="session-name">Nome da conexão</label>
              <input id="session-name" value={name} onChange={(event) => setName(event.target.value)} />
              <span className="field__hint">Apenas para identificação no painel.</span>
            </div>

            <button
              className="btn btn--primary"
              disabled={!isAdmin || busy || !name.trim()}
              onClick={() => void act(() => api.createWhatsappSession(name.trim()))}
            >
              Criar conexão
            </button>
          </div>
        )}

        {sessions.map((session) => (
          <div key={session.id} className="card">
            <div className="btn-row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <h3 className="card__title">{session.name}</h3>
                <p className="card__subtitle" style={{ margin: 0 }}>
                  {session.phoneNumber ? (
                    // Agrupado e monoespaçado: é o número que o cliente vê ao
                    // receber a mensagem, e alguém vai conferi-lo dígito a dígito.
                    <>
                      Número: <span className="mono">{formatPhone(session.phoneNumber)}</span>
                    </>
                  ) : (
                    'Nenhum número vinculado'
                  )}
                </p>
              </div>
              <StatusBadge status={session.status} />
            </div>

            {session.lastError && <div className="alert alert--error">{session.lastError}</div>}

            {session.status === 'qr_required' && session.qrCode && (
              <div style={{ margin: '12px 0' }}>
                <img className="qr" src={session.qrCode} alt="QR code para vincular o WhatsApp" />
                <p className="field__hint" style={{ textAlign: 'center', marginTop: 8 }}>
                  No celular: WhatsApp → Dispositivos conectados → Conectar dispositivo. O código é
                  atualizado automaticamente enquanto esta tela estiver aberta.
                </p>
              </div>
            )}

            {session.status === 'connected' && (
              <div className="alert alert--success">
                Conectado{session.connectedAt && ` desde ${new Date(session.connectedAt).toLocaleString('pt-BR')}`}.
                As mensagens recebidas já aparecem em Atendimentos.
              </div>
            )}

            {session.status === 'connected' && <VoiceSettings session={session} isAdmin={isAdmin} />}

            <div className="btn-row">
              {session.status !== 'connected' && (
                <button
                  className="btn btn--primary"
                  disabled={!isAdmin || busy}
                  onClick={() => void act(() => api.connectSession(session.id))}
                >
                  {session.status === 'qr_required' ? 'Gerar novo QR' : 'Conectar'}
                </button>
              )}
              {session.status === 'connected' && (
                <button
                  className="btn"
                  disabled={!isAdmin || busy}
                  onClick={() => void act(() => api.disconnectSession(session.id))}
                >
                  Desconectar
                </button>
              )}
              <button
                className="btn btn--danger"
                disabled={!isAdmin || busy}
                onClick={() => void act(() => api.logoutSession(session.id))}
              >
                Desvincular número
              </button>
            </div>
            <p className="field__hint" style={{ marginTop: 8 }}>
              Desconectar apenas encerra a sessão neste servidor — o pareamento continua e a conexão volta
              sozinha. Desvincular apaga as credenciais e exige ler o QR novamente.
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: WhatsappStatus }) {
  const variant =
    status === 'connected'
      ? 'ready'
      : status === 'error' || status === 'logged_out'
        ? 'failed'
        : status === 'qr_required' || status === 'connecting'
          ? 'pending'
          : 'closed';

  return <span className={`badge badge--${variant}`}>{STATUS_LABELS[status]}</span>;
}

/**
 * Configuração das chamadas de voz (Wavoip) desta conexão.
 *
 * O token é do dispositivo criado no painel do Wavoip. Depois de salvo, a URL
 * de webhook devolvida precisa ser cadastrada lá — é ela que traz de volta as
 * chamadas perdidas, que nenhum navegador aberto teria como registrar.
 */
function VoiceSettings({ session, isAdmin }: { session: WhatsappSession; isAdmin: boolean }) {
  const [token, setToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState<string | null>(session.wavoipWebhookUrl);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);

  useEffect(() => {
    setWebhookUrl(session.wavoipWebhookUrl);
  }, [session.wavoipWebhookUrl]);

  useEffect(() => () => {
    if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.configureCallDevice(session.id, token.trim());
      setWebhookUrl(result.webhookUrl);
      setToken('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!webhookUrl) return;
    await navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    copyTimer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <div className="btn-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Chamadas de voz</strong>
        <VoiceBadge session={session} />
      </div>

      <p className="field__hint" style={{ marginBottom: 10 }}>
        Crie um dispositivo no painel do Wavoip e cole o token aqui. Não é preciso ler outro QR: a
        chamada usa esta mesma conexão do WhatsApp.
      </p>

      {error && <div className="alert alert--error">{error}</div>}

      <div className="btn-row">
        <input
          type="password"
          placeholder={session.wavoipConfigured ? 'Token configurado — cole outro para trocar' : 'Token do dispositivo Wavoip'}
          value={token}
          disabled={!isAdmin || saving}
          onChange={(event) => setToken(event.target.value)}
          style={{ flex: 1, padding: '7px 9px', minWidth: 220 }}
        />
        <button className="btn btn--primary" disabled={!isAdmin || saving || !token.trim()} onClick={() => void save()}>
          {saving ? 'Salvando' : 'Salvar token'}
        </button>
      </div>

      {webhookUrl && (
        <div style={{ marginTop: 10 }}>
          <div className="field__hint" style={{ marginBottom: 4 }}>
            Cadastre esta URL de webhook no painel do Wavoip (eventos de chamada):
          </div>
          <div className="btn-row">
            <code
              className="mono"
              style={{
                flex: 1,
                padding: '6px 8px',
                background: 'var(--surface-2)',
                borderRadius: 'var(--radius-sm)',
                overflowWrap: 'anywhere',
              }}
            >
              {webhookUrl}
            </code>
            <button className="btn btn--sm" onClick={() => void copy()}>
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VoiceBadge({ session }: { session: WhatsappSession }) {
  if (!session.wavoipConfigured) return <span className="badge badge--closed">Não configurado</span>;
  if (session.wavoipStatus === 'connected') return <span className="badge badge--ready">Ativas</span>;
  if (session.wavoipStatus === 'error') return <span className="badge badge--failed">Com erro</span>;
  return <span className="badge badge--pending">Conectando</span>;
}
